import React from "react";
import { IconChevronLeft, IconChevronRight } from "../shared/icons/IconLib";

const VIEW_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "future", label: "Future" },
  { value: "all", label: "All Time" },
];

const btnStyle = {
  padding: "6px 12px",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const selectStyle = {
  padding: "6px 10px",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
  backgroundColor: "#fff",
  cursor: "pointer",
  minWidth: "118px",
  fontSize: "14px",
  color: "#212529",
};

/** Shared “one group” chrome (standalone panel uses larger padding). */
const groupedChrome = {
  boxSizing: "border-box",
  backgroundColor: "#f8f9fa",
  border: "1px solid #dee2e6",
  borderRadius: "8px",
};

/**
 * Global date range + prev/next for Events / Tasks / Projects.
 * @param {'panel'|'inline'|'stacked'} [variant='panel'] — `inline`: compact toolbar; `stacked`: vertical filter-panel layout.
 * @param {boolean} [hideCenterTitle=false] — inline only: hide center range title (toolbar uses dropdown).
 */
export default function GlobalTimeNavigatorBar({
  title,
  view,
  onViewChange,
  onToday,
  onPrev,
  onNext,
  rightSlot,
  variant = "panel",
  hideCenterTitle = false,
  viewOptions,
}) {
  const isInline = variant === "inline";
  const isStacked = variant === "stacked";
  const titleStr = title != null ? String(title) : "";
  const allowedViews = Array.isArray(viewOptions) && viewOptions.length > 0
    ? new Set(viewOptions)
    : null;
  const visibleViewOptions = allowedViews
    ? VIEW_OPTIONS.filter((option) => allowedViews.has(option.value))
    : VIEW_OPTIONS;
  const selectedView = visibleViewOptions.some((option) => option.value === view)
    ? view
    : (visibleViewOptions[0]?.value || "month");
  const rangeSelect = isInline ? (
    <select
      aria-label="Time range (day, week, month, future, or all time)"
      className="toolbar-time-nav-inline__select"
      value={selectedView}
      onChange={(e) => onViewChange(e.target.value)}
    >
      {visibleViewOptions.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  ) : isStacked ? (
    <select
      aria-label="Time range (day, week, month, future, or all time)"
      className="input global-time-navigator-stacked__range"
      value={selectedView}
      onChange={(e) => onViewChange(e.target.value)}
    >
      {visibleViewOptions.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  ) : (
    <select
      aria-label="Time range (day, week, month, future, or all time)"
      value={selectedView}
      onChange={(e) => onViewChange(e.target.value)}
      style={selectStyle}
    >
      {visibleViewOptions.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );

  const titleStyle = isInline
    ? undefined
    : {
        minWidth: "200px",
        textAlign: "center",
        fontWeight: 600,
      };

  const inlineBtnClass = "toolbar-time-nav-inline__btn";
  const inlineStepBtnClass = `${inlineBtnClass} toolbar-time-nav-inline__btn--step`;

  const core = (
    <>
      <button
        type="button"
        onClick={onToday}
        className={isInline ? inlineBtnClass : undefined}
        style={isInline ? undefined : btnStyle}
        title={isInline ? "Today" : undefined}
      >
        Today
      </button>
      <button
        type="button"
        onClick={onPrev}
        className={isInline ? inlineStepBtnClass : undefined}
        style={isInline ? undefined : btnStyle}
        title="Previous"
        aria-label="Previous"
      >
        {isInline ? <IconChevronLeft size={18} /> : "‹ Prev"}
      </button>
      <span
        className={isInline ? "toolbar-time-nav-inline__title" : undefined}
        style={titleStyle}
        title={titleStr || undefined}
      >
        {title}
      </span>
      <button
        type="button"
        onClick={onNext}
        className={isInline ? inlineStepBtnClass : undefined}
        style={isInline ? undefined : btnStyle}
        title="Next"
        aria-label="Next"
      >
        {isInline ? <IconChevronRight size={18} /> : "Next ›"}
      </button>
      {isInline ? (
        <>
          {rightSlot}
          {rangeSelect}
        </>
      ) : (
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {rightSlot}
          {rangeSelect}
        </div>
      )}
    </>
  );

  if (isStacked) {
    const titleStr = title != null ? String(title) : "";
    return (
      <div className="global-time-navigator-stacked">
        {rangeSelect}
        <div className="global-time-navigator-stacked__nav">
          <button
            type="button"
            onClick={onPrev}
            className="global-time-navigator-stacked__step"
            title="Previous"
            aria-label="Previous"
          >
            <IconChevronLeft size={18} />
          </button>
          <span className="global-time-navigator-stacked__title" title={titleStr || undefined}>
            {title}
          </span>
          <button
            type="button"
            onClick={onNext}
            className="global-time-navigator-stacked__step"
            title="Next"
            aria-label="Next"
          >
            <IconChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  if (isInline) {
    const inlineClass = [
      "global-time-navigator-inline",
      "global-time-navigator-inline--boxed",
      "toolbar-time-nav-inline",
      "toolbar-time-nav-inline--boxed",
      hideCenterTitle ? "toolbar-time-nav-inline--compact-title" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return <div className={inlineClass}>{core}</div>;
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "10px",
        padding: "12px",
        ...groupedChrome,
      }}
    >
      {core}
    </div>
  );
}
